<script setup lang="ts">
    import type { SelectableTab } from '~/components/shared/SelectableTabButtons.vue';
    import type { AcademyTrainingsListingSort } from '~/types/academy/trainings-listing-sort';
    import { STATUS_FILTER_CODE } from '~/composables/useAcademyTrainingsListing';

    const { disabled = false } = defineProps<{
        disabled?: boolean;
    }>();

    const emit = defineEmits<{
        'toggle-filter': [];
        'reset-filters': [];
    }>();

    const { t } = useI18n();

    type CourseStatus = 'all' | 'open' | 'completed';
    const { filterState, catalogSort, toggleCheckboxes, toggleCheckboxesInverse } = useAcademyTrainingsListing();
    const selectedCourseStatus = ref<CourseStatus>('all');
    const selectedSort = ref<AcademyTrainingsListingSort | undefined>();

    const trainingTabs = computed<SelectableTab[]>(() => [
        { key: 'all', label: t('academy.trainings.tabs.all') },
        { key: 'open', label: t('academy.trainings.tabs.open') },
        { key: 'completed', label: t('academy.trainings.tabs.completed') }
    ]);

    watch(selectedSort, (sort) => {
        if (sort) {
            catalogSort.value = sort;
        }
    });

    watch(selectedCourseStatus, (status) => {
        switch (status) {
            case 'open':
                toggleCheckboxesInverse(STATUS_FILTER_CODE, ['completed']);
                break;
            case 'completed':
                toggleCheckboxes(STATUS_FILTER_CODE, ['completed']);
                break;
            case 'all':
            default:
                toggleCheckboxes(STATUS_FILTER_CODE, []);
                break;
        }
    });

    watch(filterState, () => {
        const enrollmentStatus = filterState.value[STATUS_FILTER_CODE];

        if (enrollmentStatus?.type !== 'checkbox_filter') {
            return;
        }

        if (enrollmentStatus.selectedKeys.length === 0) {
            selectedCourseStatus.value = 'all';
        }
    });
</script>

<template>
    <div class="toolbar">
        <PrimaryButton
            :button-text="$t('academy.listing.filter')"
            product-button
            has-icon
            ghost
            variant="secondary"
            class="filter-btn"
            :disabled="disabled"
            @click="!disabled && emit('toggle-filter')"
        >
            <template #customIcon>
                <Icon
                    name="lucide:filter"
                    mode="svg"
                />
            </template>
        </PrimaryButton>

        <div class="course-status-tabs-wrapper">
            <SelectableTabButtons
                v-model="selectedCourseStatus"
                :tabs="trainingTabs"
                :aria-label="$t('academy.trainings.tabs.ariaLabel')"
                class="course-status-tabs"
                :disabled="disabled"
            />

            <AcademyTrainingsListingSortSelect @update:sort="selectedSort = $event" />
        </div>
        <SharedTextIconButton
            class="reset-filters-btn"
            stretch
            :label="$t('academy.listing.resetFilters')"
            @click="$emit('reset-filters')"
        />
    </div>
</template>

<style lang="scss" scoped>
    .toolbar {
        display: flex;
        flex-direction: column;
        gap: var(--spacing);
        background-color: var(--brand-primary-10);
        padding: var(--spacing);
        border-radius: var(--border-radius-s);
        margin-bottom: var(--spacing--2);

        @media (min-width: $br-l) {
            background-color: transparent;
            padding: 0;
            border-radius: 0;
        }

        .filter-btn {
            width: 100%;
            justify-content: space-between;
            background-color: var(--white);
            color: var(--black);

            @media (min-width: $br-l) {
                display: none;
            }
        }

        .reset-filters-btn {
            @media (min-width: $br-l) {
                display: none;
            }
        }

        .course-status-tabs-wrapper {
            display: flex;
            flex-direction: column;
            gap: var(--spacing--2);

            @media (min-width: $br-l) {
                flex-direction: row;
                align-items: center;
                justify-content: space-between;
            }
        }
    }
</style>
